import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that renders a paginated blog posts list with button-based loading
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The grid element.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLInputElement} [searchInput] - The search input element.
 * @property {HTMLElement} [searchWrapper] - The search wrapper element.
 * @property {HTMLElement} [noResults] - The no results message element.
 * @property {HTMLButtonElement} [searchClear] - The search clear button.
 *
 * @extends Component<Refs>
 */
export default class BlogPostsList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {boolean} */
  #isLoading = false;

  /** @type {number | null} */
  #searchTimeout = null;

  /** @type {string | null} */
  #originalGridHTML = null;

  /** @type {AbortController | null} */
  #searchAbortController = null;

  /** @type {boolean} */
  #isSearchActive = false;

  connectedCallback() {
    super.connectedCallback();

    // Check for search query in URL (from predictive search "View All")
    const urlQuery = new URL(window.location.href).searchParams.get('q');
    if (urlQuery && this.refs.searchInput) {
      this.refs.searchInput.value = urlQuery;
      if (this.refs.searchClear) this.refs.searchClear.hidden = false;
      this.#performSearch();
    }

    // Pre-fetch next page
    this.#fetchNextPage();

    // Add click handler for load more button
    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', this.#handleLoadMoreClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.removeEventListener('click', this.#handleLoadMoreClick);
    }

    if (this.#searchTimeout) {
      clearTimeout(this.#searchTimeout);
    }

    this.#searchAbortController?.abort();
  }

  /**
   * Smooth-scrolls to the footer newsletter section
   * @param {Event} event
   */
  scrollToNewsletter(event) {
    event.preventDefault();
    const target =
      document.querySelector('footer .email-signup-block') ||
      document.querySelector('footer [id*="email"]') ||
      document.querySelector('#footer-newsletter');

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleSearch() {
    if (this.#searchTimeout) {
      clearTimeout(this.#searchTimeout);
    }

    this.#searchTimeout = setTimeout(() => {
      this.#performSearch();
    }, 300);
  }

  clearSearch() {
    const { searchInput } = this.refs;
    if (searchInput) {
      searchInput.value = '';
    }
    this.#restoreOriginalGrid();
  }

  async #performSearch() {
    const { searchInput, grid, noResults, searchClear } = this.refs;
    if (!searchInput || !grid) return;

    const query = searchInput.value.trim();

    // Toggle clear button visibility
    if (searchClear) {
      searchClear.hidden = !query;
    }

    if (!query) {
      this.#restoreOriginalGrid();
      return;
    }

    if (query.length < 2) return;

    // Cache original grid before first search
    if (!this.#originalGridHTML) {
      this.#originalGridHTML = grid.innerHTML;
    }

    // Abort previous request
    this.#searchAbortController?.abort();
    this.#searchAbortController = new AbortController();
    const { signal } = this.#searchAbortController;

    try {
      const url = new URL(
        `${window.Shopify?.routes?.root || '/'}search/suggest.json`,
        location.origin
      );
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'article');
      url.searchParams.set('resources[limit]', '20');
      url.searchParams.set('resources[options][fields]', 'title,body,tag');

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return;

      const data = await response.json();
      if (signal.aborted) return;

      const articles = data.resources?.results?.articles || [];

      this.#isSearchActive = true;

      // Hide pagination
      const pagination = this.querySelector('.blog-pagination');
      if (pagination) {
        pagination.style.display = 'none';
      }

      if (articles.length === 0) {
        grid.replaceChildren();
        if (noResults) noResults.hidden = false;
        return;
      }

      if (noResults) noResults.hidden = true;

      // Build search result cards using DOM methods
      const fragment = document.createDocumentFragment();
      for (const article of articles) {
        fragment.appendChild(this.#buildArticleCard(article));
      }
      grid.replaceChildren(fragment);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Blog search failed:', error);
    }
  }

  /**
   * Builds a blog card element from predictive search article data
   * @param {object} article - Article data from predictive search API
   * @returns {HTMLElement}
   */
  #buildArticleCard(article) {
    const card = document.createElement('article');
    card.className = 'blog-card';

    const link = document.createElement('a');
    link.href = article.url;
    link.className = 'blog-card__link';

    // Image wrapper
    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'blog-card__image-wrapper';

    if (article.image) {
      const img = document.createElement('img');
      const imageUrl = article.image.includes('?') ? `${article.image}&width=450` : `${article.image}?width=450`;
      img.src = imageUrl;
      img.loading = 'lazy';
      img.width = 450;
      img.height = 300;
      img.sizes = '(min-width: 1000px) 33vw, (min-width: 600px) 50vw, 100vw';
      img.alt = article.title;
      img.className = 'blog-card__image';
      imageWrapper.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'blog-card__placeholder';
      placeholder.innerHTML =
        '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>' +
        '<circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>' +
        '<path d="M3 16L8 11L11 14L16 9L21 14V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V16Z" fill="currentColor" opacity="0.3"/>' +
        '</svg>';
      imageWrapper.appendChild(placeholder);
    }

    if (article.tags && article.tags.length > 0) {
      const pill = document.createElement('span');
      pill.className = `blog-card__pill${!article.image ? ' blog-card__pill--placeholder' : ''}`;
      pill.textContent = article.tags[0];
      imageWrapper.appendChild(pill);
    }

    link.appendChild(imageWrapper);

    // Content
    const content = document.createElement('div');
    content.className = 'blog-card__content';

    const text = document.createElement('div');
    text.className = 'blog-card__text';

    const title = document.createElement('h2');
    title.className = 'blog-card__title';
    title.textContent = article.title;
    text.appendChild(title);

    if (article.body) {
      const excerpt = document.createElement('p');
      excerpt.className = 'blog-card__excerpt';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = article.body;
      excerpt.textContent = (tempDiv.textContent || '').substring(0, 170);
      text.appendChild(excerpt);
    }

    content.appendChild(text);

    if (article.published_at) {
      const time = document.createElement('time');
      time.className = 'blog-card__date';
      time.dateTime = article.published_at;
      time.textContent = new Date(article.published_at).toLocaleDateString('cs-CZ', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      content.appendChild(time);
    }

    link.appendChild(content);
    card.appendChild(link);

    return card;
  }

  #restoreOriginalGrid() {
    const { grid, noResults, searchClear } = this.refs;

    if (searchClear) {
      searchClear.hidden = true;
    }

    if (!this.#isSearchActive) return;

    this.#isSearchActive = false;

    if (grid && this.#originalGridHTML) {
      const temp = document.createElement('div');
      temp.innerHTML = this.#originalGridHTML;
      grid.replaceChildren(...temp.childNodes);
    }

    if (noResults) {
      noResults.hidden = true;
    }

    // Restore pagination
    const pagination = this.querySelector('.blog-pagination');
    if (pagination) {
      pagination.style.display = '';
    }
  }

  #handleLoadMoreClick = async () => {
    if (this.#isLoading) return;

    this.#isLoading = true;

    const { loadMoreButton } = this.refs;
    const originalText = loadMoreButton?.dataset.originalText || 'Zobrazit další';

    if (loadMoreButton) {
      loadMoreButton.disabled = true;
      loadMoreButton.textContent = 'Načítám...';
    }

    try {
      const hasMorePages = await this.#renderNextPage();

      // Only reset button if there are more pages (otherwise pagination is hidden)
      if (hasMorePages && loadMoreButton) {
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = originalText;
      }
    } catch (error) {
      console.error('Failed to load more blog posts:', error);
      // Reset button on error so user can retry
      if (loadMoreButton) {
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = originalText;
      }
    } finally {
      this.#isLoading = false;
    }
  };

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    // Query DOM directly to get fresh card list (refs may be stale after append)
    const { grid } = this.refs;
    if (!grid) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    if (cards.length === 0) return;

    const lastCard = cards[cards.length - 1];
    if (!lastCard) return;

    const currentPage = Number(/** @type {HTMLElement} */ (lastCard).dataset.page);
    const nextPage = currentPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    return {
      page: nextPage,
      url,
    };
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) {
      return false;
    }

    return true;
  }

  async #fetchNextPage() {
    const nextPage = this.#getNextPage();

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      nextPage.url
    );
    this.pages.set(nextPage.page, pageContent);
  }

  /**
   * Renders the next page of blog posts
   * @returns {Promise<boolean>} True if there are more pages to load
   */
  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const nextPage = this.#getNextPage();

    if (!nextPage || !this.#shouldUsePage(nextPage)) return false;

    let nextPageElements = this.#getGridForPage(nextPage.page);

    // If not cached, fetch it
    if (!nextPageElements) {
      await this.#fetchNextPage();
      nextPageElements = this.#getGridForPage(nextPage.page);
      if (!nextPageElements) return false;
    }

    // Append new items to grid
    grid.append(...nextPageElements);

    // Update URL
    history.pushState('', '', nextPage.url.toString());

    // Update progress bar
    this.#updateProgressBar();

    // Check if there are more pages
    const newNextPage = this.#getNextPage();
    const hasMorePages = newNextPage && this.#shouldUsePage(newNextPage);

    if (hasMorePages) {
      // Pre-fetch the next page
      requestIdleCallback(() => {
        this.#fetchNextPage();
      });
    } else {
      // Hide the pagination section when no more pages
      const paginationSection = this.querySelector('.blog-pagination');
      if (paginationSection) {
        paginationSection.style.display = 'none';
      }
    }

    return hasMorePages;
  }

  #updateProgressBar() {
    const { grid } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage);
    // Query DOM directly to get fresh card list (refs may be stale after append)
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');

    if (cards.length === 0) return;

    const lastCard = /** @type {HTMLElement} */ (cards[cards.length - 1]);
    const currentPage = Number(lastCard?.dataset.page || 1);

    // Update progress text
    const progressText = this.querySelector('.blog-pagination__text');
    if (progressText) {
      const articlesPerPage = Math.ceil(cards.length / currentPage);
      const totalArticles = cards.length;
      const blogArticlesCount =
        Number(grid.dataset.totalArticles) || lastPage * articlesPerPage;

      progressText.textContent = `Viděli jste ${totalArticles} z ${blogArticlesCount} článků`;
    }

    // Update progress bar fill
    const progressFill = this.querySelector('.blog-pagination__bar-fill');
    if (progressFill) {
      const percentage = (currentPage / lastPage) * 100;
      progressFill.style.width = `${percentage}%`;
    }
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
